<template>
  <q-list class="reminder-group">
    <slot name="model" />

    <q-list
      v-if="reminders.length > 0"
    >
      <EditableReminderItem
        v-for="(reminder, key) in reminders"
        :key="key"
        :index="key"
        :reminder="reminder"
        :real-time="realTime"
        :disable="disable"
        :default-email="defaultEmail"
        :default-phone="defaultPhone"
        :hide-title="hideRowTitle"
        :disable-type="disableType"
        :minute-picker-props="minutePickerProps"
        :email-suggestions="emailSuggestions"
        :phone-suggestions="phoneSuggestions"
        :email-prefill-hint="emailPrefillHint"
        :phone-prefill-hint="phonePrefillHint"
        @remove="removeReminder(key, reminder)"
        @email-focus="$emit('emailFocus')"
        @phone-focus="$emit('phoneFocus')"
      />
    </q-list>
    <slot v-else name="no-results" />

    <q-item v-if="!disable && canAddReminder">
      <q-btn
        :label="addLabel"
        size="12px"
        icon="mdi-plus"
        no-caps
        color="blue-7"
        @click="$emit('add')"
      />
    </q-item>
  </q-list>
</template>

<script>
import EditableReminderItem from 'components/partials/communications/reminders/EditableReminderItem'

/**
 * ReminderList is the outer encapsulating container for all reminder items
 *
 * SLOTS:
 * - model: Model item to display at top of reminder list (typically a ClientItem etc)
 * - no-results: Content to display if there are no results
 *
 * EVENTS:
 * - add: button clicked to create new reminder in group
 * - remove ({ value, key }): reminder in group is clicked to remove
 * - email-focus: focus is placed on the EmailInput field (typically used for fetching email suggestions from VueX)
 * - phone-focus: focus is placed on the MobilePhoneInput field (typically used for fetching mobile suggestions from VueX)
 */
export default {
  components: {
    EditableReminderItem
  },
  props: {
    // Limit number of reminders which can be created in this group (null = unlimited)
    limit: {
      type: Number,
      default: null
    },
    // List of reminders in this group
    reminders: {
      type: Array,
      required: true
    },
    // real-time mode - disables reminder_offset dropdown and forces limit of 1 reminder to this group
    realTime: {
      type: Boolean,
      default: false
    },
    // Disable interacting/editing reminders in this group
    disable: {
      type: Boolean,
      default: false
    },
    // Default mobile number to use when SMS option is selected
    defaultPhone: {
      type: String
    },
    // Default email to use when email option is selected
    defaultEmail: {
      type: String
    },
    // Hint to display for prefilled email address
    emailPrefillHint: {
      type: String
    },
    // Hint to display for prefilled mobile number
    phonePrefillHint: {
      type: String
    },
    // Label to display for adding a new reminder
    addLabel: {
      type: String,
      default: 'Add Reminder'
    },
    // Hide title for each reminder row ("Reminder 1" etc)
    hideRowTitle: {
      type: Boolean,
      default: false
    },
    // Disable the type field (SMS, EMAIL etc)
    disableType: {
      type: Boolean,
      default: false
    },
    // Any props to bind to the MinutePicker
    minutePickerProps: {
      type: Object,
      default: () => {
        return {}
      }
    },
    // Array of email suggestions to display in a dropdown within EmailInput
    emailSuggestions: {
      type: Array,
      default: () => {
        return []
      }
    },
    // Array of mobile suggestions to display in a dropdown within MobilePhoneInput
    phoneSuggestions: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  emits: ['emailFocus', 'phoneFocus', 'add', 'remove'],
  computed: {
    canAddReminder () {
      const limit = this.realTime ? 1 : this.limit
      if (limit === null) {
        return true
      }
      return limit > this.reminders.length
    }
  },
  methods: {
    removeReminder (key, value) {
      this.$emit('remove', { value, key })
    }
  }
}
</script>
